import { createFileRoute, Link } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { PlatformCard } from '@/features/fraud/admin/platform-card'
import { AdminEventsTable } from '@/features/fraud/admin/events-table'
import { AdminAttendees } from '@/features/fraud/admin/attendees-admin'
import { AdminBlocks } from '@/features/fraud/admin/blocks-admin'
import { AdminChargebacks } from '@/features/fraud/admin/chargebacks-admin'

const TABS = ['overview', 'attendees', 'blocks', 'chargebacks'] as const
type Tab = (typeof TABS)[number]
const TAB_LABEL: Record<Tab, string> = { overview: 'Overview', attendees: 'Attendees', blocks: 'Blocks', chargebacks: 'Chargebacks' }

export const Route = createFileRoute('/_authenticated/ops/fraud')({
  // Superuser gating lives on the /ops layout route (ops.tsx); every
  // endpoint behind this page is staff-only server-side as well.
  component: FraudAdminPage,
  validateSearch: (search: Record<string, unknown>): { tab?: Tab; user?: number; dispute?: number } => {
    const tab = search.tab
    const user = Number(search.user)
    const dispute = Number(search.dispute)
    return {
      ...(typeof tab === 'string' && (TABS as readonly string[]).includes(tab) ? { tab: tab as Tab } : {}),
      ...(Number.isFinite(user) && user > 0 ? { user } : {}),
      ...(Number.isFinite(dispute) && dispute > 0 ? { dispute } : {}),
    }
  },
})

function FraudAdminPage() {
  const { tab = 'overview', user, dispute } = Route.useSearch()
  const navigate = Route.useNavigate()

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Risk</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
          Platform-wide: the rollout switch, every event's posture, attendees across events, account-wide blocks and all chargebacks.
        </p>
      </div>

      <nav className="flex gap-1 border-b border-neutral-200 dark:border-neutral-800 overflow-x-auto">
        {TABS.map((t) => (
          <Link
            key={t}
            to="/ops/fraud"
            search={t === 'overview' ? {} : { tab: t }}
            className={cn('px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px',
              tab === t
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 font-medium'
                : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white')}
          >
            {TAB_LABEL[t]}
          </Link>
        ))}
      </nav>

      {tab === 'overview' && (
        <>
          <PlatformCard />
          <AdminEventsTable />
        </>
      )}
      {tab === 'attendees' && <AdminAttendees key={user ?? 'search'} initialUserId={user} />}
      {tab === 'blocks' && <AdminBlocks />}
      {tab === 'chargebacks' && (
        <AdminChargebacks
          openDisputeId={dispute ?? null}
          onOpenDispute={(id) => navigate({ search: id ? { tab: 'chargebacks', dispute: id } : { tab: 'chargebacks' } })}
        />
      )}
    </div>
  )
}
